import type { ApiResponse } from "@entrilia/shared/api/types";
import type {
  AnyReportConfigurationForRunning,
  ApiReportResponse,
  BuildChartReportRequest,
  BuildExportRequest,
  BuildFinancialStatementReportRequest,
  BuildListReportRequest,
  BuildPivotReportRequest,
  BuildTabularReportRequest,
  BuildTileReportRequest,
  CancellationToken,
  ChangeReportGroupRequest,
  ChartResponse,
  DeleteReportsRequest,
  ExportResponse,
  FinancialStatementResponse,
  ListDataResponse,
  PivotDataResponse,
  RenameReportRequest,
  Report,
  ReportAuthorizationInfo,
  ReportHistoryItem,
  ReportInfo,
  ReportType,
  RunApiReportRequest,
  RunExportRequest,
  RunFinancialStatementReportRequest,
  RunListReportRequest,
  RunPivotReportRequest,
  RunTabularReportRequest,
  TabularDataResponse,
  TileDataResponse,
} from "../../../../../api/types/svTypes.ts";
import type { BiApiClientBase } from "./BiApiClientProviderContext.ts";
import { useBiApiClientProvider } from "./BiApiClientProviderContext.ts";

export interface BiApiClient extends BiApiClientBase {
  updateReport: (report: Report) => Promise<ApiResponse<Report>>;
  addReport: (report: Report) => Promise<ApiResponse<Report>>;
  createDraftReport: () => Promise<ApiResponse<Report>>;
  getReport: (reportId: string, mode: "run" | "edit") => Promise<ApiResponse<Report>>;
  runTabularReport: <
    TBody extends RunTabularReportRequest | RunApiReportRequest,
    TResponse extends TabularDataResponse | ApiReportResponse,
  >(
    dataSetId: string,
    body: TBody,
    done: (data: ApiResponse<TResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  buildTabularReport: <
    TBody extends BuildTabularReportRequest,
    TResponse extends TabularDataResponse | ApiReportResponse,
  >(
    body: TBody,
    done: (data: ApiResponse<TResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  buildCashFlowReport?: BiApiClient["buildTabularReport"];
  runCashFlowReport?: BiApiClient["runTabularReport"];
  buildTileReport?: (
    body: BuildTileReportRequest,
    done: (data: ApiResponse<TileDataResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  buildChartReport?: (
    body: BuildChartReportRequest,
    done: (data: ApiResponse<ChartResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  runPivotReport?: (
    dataSetId: string,
    body: RunPivotReportRequest,
    done: (data: ApiResponse<PivotDataResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  buildPivotReport?: (
    body: BuildPivotReportRequest,
    done: (data: ApiResponse<PivotDataResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  runListReport?: (
    dataSetId: string,
    body: RunListReportRequest,
    signal?: AbortSignal
  ) => Promise<ApiResponse<ListDataResponse>>;
  buildListReport?: (body: BuildListReportRequest, signal?: AbortSignal) => Promise<ApiResponse<ListDataResponse>>;
  runFinancialStatementReport?: (
    dataSetId: string,
    body: RunFinancialStatementReportRequest,
    signal?: AbortSignal
  ) => Promise<ApiResponse<FinancialStatementResponse>>;
  buildFinancialStatementReport?: (
    body: BuildFinancialStatementReportRequest,
    signal?: AbortSignal
  ) => Promise<ApiResponse<FinancialStatementResponse>>;
  renameReport: (request: RenameReportRequest) => Promise<ApiResponse<Report>>;
  changeReportGroup: (request: ChangeReportGroupRequest) => Promise<ApiResponse<Report>>;
  deleteReports: (request: DeleteReportsRequest) => Promise<ApiResponse<boolean>>;
  getReportAuthorizationInfo: (reportId: string) => Promise<ApiResponse<ReportAuthorizationInfo>>;
  getReportHistory: (reportId: string, signal?: AbortSignal) => Promise<ApiResponse<ReportHistoryItem[]>>;
  getReports: ({
    reportIds,
    reportType,
  }: {
    reportIds?: string[];
    reportType?: ReportType;
  }) => Promise<ApiResponse<ReportInfo[]>>;
  getReportsUrl: (clientCode: string) => string;
  getReportConfigurationForRunning: (reportId: string) => Promise<ApiResponse<AnyReportConfigurationForRunning>>;
  requestRunTabularExport?: (body: RunExportRequest) => Promise<ApiResponse<ExportResponse>>;
  requestBuildTabularExport?: (body: BuildExportRequest) => Promise<ApiResponse<ExportResponse>>;
  requestRunPivotExport?: (body: RunExportRequest) => Promise<ApiResponse<ExportResponse>>;
  requestBuildPivotExport?: (body: BuildExportRequest) => Promise<ApiResponse<ExportResponse>>;
  requestRunListExport?: (body: RunExportRequest) => Promise<ApiResponse<ExportResponse>>;
  requestBuildListExport?: (body: BuildExportRequest) => Promise<ApiResponse<ExportResponse>>;
}

export const useEnhancedBiApiClientProvider = () => {
  return useBiApiClientProvider<BiApiClient>();
};
